import Icon from '@ant-design/icons';
import { Checkbox, Divider, Popover, message } from 'antd';
import classNames from 'classnames';
import { Fragment, useEffect, useMemo, useState } from 'react';

import AddIcon from '@/components/Icon/svgs/add.svg';
import TopicIcon from '@/components/Icon/svgs/topic.svg';
import TopicModal from '@/components/memory-container/components/topicModal';
import useGenius from '@/hooks/useGenius';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import { type ISmartTopicBasic, bindNoteTopic } from '@/service/smart-topic';
import useNoteItemStore from '@/store/useNoteItemStore';
import useSmartTopicStore from '@/store/useSmartTopicStore';

import styles from './index.less';

interface IProps {
  sourceId: number;
  topics?: ISmartTopicBasic[];
  handleClick?: () => void;
  className?: string;
}

const TopicSelect = (props: IProps) => {
  const { topics: _originTopics, sourceId } = props;
  const [layoutSize] = useStorage('layoutSize');
  const { t } = useTranslation();

  const originTopics = useMemo(() => {
    if (_originTopics?.length) {
      return _originTopics.map((item) => item.id);
    }

    return [];
  }, [_originTopics]);

  const [selectedTopicIds, setSelectedTopicIds] = useState<number[]>([]);
  const [expand, setExpand] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);

  const { shadowMindId: mindId } = useGenius();
  const topicList = useSmartTopicStore((state) => state.topicList);
  const updateNote = useNoteItemStore((state) => state.updateNote);

  useEffect(() => {
    setSelectedTopicIds(originTopics);
  }, [originTopics]);

  // 选中的topic
  const selectedTopicsList = useMemo(() => {
    const _select = topicList.filter((item) => selectedTopicIds.includes(item.id));

    if (!topicList.length) {
      return _originTopics || [];
    }

    return _select;
  }, [topicList, _originTopics, selectedTopicIds]);

  const renderTopics = () => {
    if (!selectedTopicsList.length) {
      return (
        <div
          className={classNames(styles.smartTopic, 'group/topic')}
          onClick={(e) => {
            setExpand(true);
            e.stopPropagation();
          }}
        >
          <Icon
            className="text-[#643E54] transition-colors group-hover/topic:text-primary-white"
            component={TopicIcon}
          />
          <Icon
            className="mx-[2px] text-[10px] text-[#643E54] transition-colors group-hover/topic:text-primary-white"
            component={AddIcon}
          />
        </div>
      );
    }

    const smartTopicClassName = classNames(
      styles.smartTopicText,
      'hover:text-primary-sub-color cursor-pointer'
    );

    return selectedTopicsList.map((item, index) => {
      return (
        <Fragment key={item.id}>
          {index >= 1 && <div className={styles.dot} />}
          <div
            className={smartTopicClassName}
            onClick={(e) => {
              e.stopPropagation();

              // 还没获取到总的list，只做展示无法操作
              if (!selectedTopicsList.length) {
                message.info('Loading data, please wait a moment.');

                return;
              }

              setExpand(true);
            }}
          >
            #{item.name}
          </div>
        </Fragment>
      );
    });
  };

  const renderPopoverTitle = () => {
    return (
      <div className={styles.popoverTitle}>
        {t('topic.selectNumber', { number: selectedTopicIds.length })}
      </div>
    );
  };

  const renderPopoverContent = () => {
    return (
      <div
        className={styles.popoverContentContainer}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className={styles.checkBoxList}>{topicList.map((item) => renderCheckbox(item))}</div>
        <Divider className="m-0" />
        <div
          className={styles.addTopic}
          onClick={() => {
            setExpand(false);
            setModalOpen(true);
          }}
        >
          <Icon className={styles.addTopicIcon} component={AddIcon} />
          <div className={styles.addTopicText}>{t('topic.newTopic')}</div>
        </div>
      </div>
    );
  };
  const renderCheckbox = (item: ISmartTopicBasic) => {
    return (
      <Checkbox
        key={item.id}
        checked={selectedTopicIds.includes(item.id)}
        className={styles.checkboxItem}
        onChange={() => {
          setSelectedTopicIds((prev) => {
            // 如果已经选中，就取消选中
            if (prev.includes(item.id)) {
              return prev.filter((id) => id !== item.id);
            }

            // 如果没有选中，就选中
            return [...prev, item.id];
          });
        }}
      >
        <div
          key={item.id}
          className={classNames(
            styles.checkboxContainer,
            layoutSize === LAYOUT_SIZE.SMALL ? '!max-w-[200px]' : ''
          )}
        >
          <img className={styles.checkboxImg} src={item.icon} />
          <div className={styles.checkboxText}>{item.name}</div>
        </div>
      </Checkbox>
    );
  };

  return (
    <>
      <Popover
        arrow={false}
        content={renderPopoverContent()}
        onOpenChange={(open) => {
          if (!open) {
            setExpand(open);
            bindNoteTopic({ smartTopicId: selectedTopicIds, sourceId: sourceId })
              .then((res) => {
                if (res.data.code === 0) {
                  mindId && updateNote(String(sourceId));
                } else {
                  throw new Error(res.data.message);
                }
              })
              .catch((err) => {
                message.error(err.message || 'Failed to bind topic');
                setSelectedTopicIds(originTopics);
              });
          }
        }}
        open={expand}
        overlayClassName={classNames(
          styles.popover,
          layoutSize === LAYOUT_SIZE.SMALL ? 'w-[280px]' : ''
        )}
        placement="bottomLeft"
        title={renderPopoverTitle()}
        trigger="click"
      >
        <div className="flex items-center gap-2 flex-wrap">{renderTopics()}</div>
      </Popover>

      <div className="absolute" onClick={(e) => e.stopPropagation()}>
        <TopicModal
          onClose={() => {
            setModalOpen(false);
          }}
          open={modalOpen}
          type="add"
        />
      </div>
    </>
  );
};

export default TopicSelect;
